import { NavigationService } from '@omnia/ui/src/lib'
import { observable } from 'mobx'
import type { Location } from 'react-router-dom-v7'
import { Pages } from '@/pages/pages'
import type { AllPagesKeys, PagesKeys, PagesType, SubPageKeys, SubPagesType } from './models/pages'
import { PagesEnum } from './models/pages'

class CurzonNavigationService extends NavigationService<PagesEnum, SubPagesType> {
    protected getOverrideSiteIdPages(): Array<keyof PagesEnum> {
        return [] // TODO:
    }

    protected getPages(): PagesType {
        return Pages
    }

    public constructor() {
        super(new PagesEnum())
    }

    public pageObs = observable.box<PagesEnum>(undefined)

    public isCurrentPage(pageKey: keyof PagesEnum): boolean {
        return this.getCurrentPage() === pageKey
    }

    public get PagesKeys() {
        return new PagesEnum()
    }

    public goToPageOrSubPage(page: keyof PagesEnum, subPage?: SubPageKeys, params?: { [key: string]: string } | null) {
        const url = this.getPageUrl(page, params, false, subPage)
        console.log('url', url)
        this.goTo(url)
    }

    public getPageOrSubpageUrl(page: PagesKeys, subPage?: SubPageKeys, params?: { [key: string]: string } | null) {
        return this.getPageUrl(page, params, false, subPage)
    }

    public getCurrentSubPage(page: keyof PagesEnum, location: Location) {
        return navigationService.Pages[page]?.subPages?.find(subPage => {
            if (!subPage.urls?.length) return false // no urls means no node in the cms

            let url = navigationService.getPageUrl(page, undefined, false, subPage.alias)
            if (subPage.params?.length) {
                url = url.replace(`:${subPage.params[0]}/`, '')
            }
            return location.pathname.includes(url)
        })
    }

    /**
     * This method returns a URL for custom subpage names where one type of subpage can exist multiple times.
     * For example, the cinema URL can have one or more subpages where the subpage is a name of a cinema:
     * i.e., `cinema/cinema-1`, `cinema/cinema-2`.
     *
     * @param page The main page type from PagesEnum
     * @param subPageName The name of the subpage (e.g., a specific cinema name)
     * @param params Optional query parameters to append to the URL
     * @returns The complete URL for the custom subpage
     *
     * @note This assumes we have 1 level nesting of sub-page
     */
    public getNamedSubPageUrl = (page: keyof PagesEnum, subPageName: string, params: Record<string, string> = {}) => {
        // `getPageUrl` returns a first subpage url, hence a logic to replace a subpage
        const pageName = `/${subPageName}`.toLowerCase()
        const url = this.getPageUrl(page).replace(/\/[^/]*\/?$/, pageName)
        return url + this.getQueryParams(params)
    }

    public goToNamedSubpage(page: keyof PagesEnum, subPage: string, params?: { [key: string]: string } | null) {
        const url = this.getNamedSubPageUrl(page, subPage, params ?? {})
        this.goTo(url)
    }

    /**
     * Check if the page or subpage is configured in the CMS
     * */
    public pageExist(pageName: AllPagesKeys, subpageName?: SubPageKeys) {
        const page = this.getPages()[pageName]
        if (!subpageName) return !!page.urls?.length

        const subpage = page.subPages?.find(subPage => subPage?.alias === subpageName)
        return !!subpage?.urls?.length
    }
}

export const navigationService = new CurzonNavigationService()
